import apiService from './apiService';
import { GET } from './executer';
import type { EventSource } from './eventsService';

export interface ActivityPeriod {
  season_year: number;
  circuit_id: string;
  start_date: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
}

export interface ActivityResponse {
  success: boolean;
  count: number;
  activities: ActivityPeriod[];
  source?: string;
}

export interface ActivityRange {
  start: Date;
  end: Date;
}

export interface ActivityAvailability {
  ranges: ActivityRange[];
}

/**
 * Convert activity periods to availability ranges for Timeline display.
 * Handles gap merging while preserving accurate data representation.
 * 
 * @param activities - Array of activity periods from the API
 * @param gapThresholdMinutes - Merge periods within this gap (default: 5 minutes)
 */
export function computeActivityRanges(
  activities: ActivityPeriod[],
  gapThresholdMinutes: number = 5
): ActivityAvailability {
  if (activities.length === 0) {
    return { ranges: [] };
  }

  // Use accurate timestamps - don't artificially extend duration
  // The Timeline component handles minimum pixel width for visibility
  const times = activities.map(a => ({
    start: new Date(a.start_time).getTime(),
    end: new Date(a.end_time).getTime(),
  })).sort((a, b) => a.start - b.start);

  const ranges: ActivityRange[] = [];
  const gapThresholdMs = gapThresholdMinutes * 60 * 1000;
  
  // Group consecutive periods within gap threshold
  let currentRange = { start: times[0].start, end: times[0].end };
  
  for (let i = 1; i < times.length; i++) {
    if (times[i].start - currentRange.end <= gapThresholdMs) {
      currentRange.end = Math.max(currentRange.end, times[i].end);
    } else {
      ranges.push({ start: new Date(currentRange.start), end: new Date(currentRange.end) });
      currentRange = { start: times[i].start, end: times[i].end };
    }
  }
  
  // Add the last range
  ranges.push({ start: new Date(currentRange.start), end: new Date(currentRange.end) });
  
  return { ranges };
}

interface ActivityQuery {
  season_year: number;
  circuit_id: string;
  start_date: string;
  source?: EventSource | 'legacy-api' | 'l2';
  bypass_cache?: boolean;
}

class ActivityService {
  /**
   * Fetch radar activity periods for an event
   */
  async getRadarActivity(query: ActivityQuery): Promise<ActivityResponse> {
    const { season_year, circuit_id, start_date, source, bypass_cache } = query;
    
    const queryParams = new URLSearchParams({
      season_year: String(season_year),
      circuit_id,
      start_date,
    });

    if (source) {
      queryParams.append('source', source);
    }
    if (bypass_cache) {
      queryParams.append('bypass_cache', 'true');
    }

    const response = await apiService.client.execute<ActivityResponse>(
      GET,
      `/api/radar-activity?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch radar activity');
    }

    return response.data;
  }

  /**
   * Fetch station activity periods for an event
   */
  async getStationActivity(query: ActivityQuery): Promise<ActivityResponse> {
    const { season_year, circuit_id, start_date, source, bypass_cache } = query;
    
    const queryParams = new URLSearchParams({
      season_year: String(season_year),
      circuit_id,
      start_date,
    });

    if (source) {
      queryParams.append('source', source);
    }
    if (bypass_cache) {
      queryParams.append('bypass_cache', 'true');
    }

    const response = await apiService.client.execute<ActivityResponse>(
      GET,
      `/api/station-activity?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch station activity');
    }

    return response.data;
  }
}

const activityService = new ActivityService();
export default activityService;

